import './i18n';
// Główna czcionka aplikacji — Inter (samohostowany wariant zmienny wg systemu z Figmy).
import '@fontsource-variable/inter';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { ErrorBoundary } from './components/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { setAccessToken } from './lib/access-token';
import { pickupHandoffKey, pickupImpersonationKey } from './lib/dev-tenant-id';
import { createQueryClient } from './lib/query';
import { buildCentralLoginUrl } from './lib/tenant-url';
import { App } from './App';

// CSS import order — Tailwind v4 FIRST (entry), Reactflow CSS SECOND.
// Inverted order powoduje że Vite dev mode czasem nie ładuje styli at first load
// (Tailwind v4 + Vite issue #16399). Reactflow CSS jest plain scoped (.react-flow__*),
// nie używa Tailwind directives — bezpieczny do osobnego importu.
import './index.css';
import '@xyflow/react/dist/style.css';

const queryClient = createQueryClient();

const root = document.getElementById('root');
if (!root) throw new Error('#root element not found');

// F8 T8.5 / ADR-0013 — przed render: jeśli URL ma `#__impSessionKey=...`, wymień klucz
// na access token i ustaw go zanim TenantAuthProvider odpali `/me` query. Bez tego
// pierwsze /me wraca 401, refresh chain unieważnia hipotetyczne stare cookie i sesja
// impersonacji nigdy nie wstaje (klucz pickup-only, jednorazowy).
async function bootstrap(): Promise<void> {
  const impersonation = await pickupImpersonationKey();
  if (impersonation && !impersonation.ok) {
    // Klucz impersonacji BYŁ w URL, ale redeem padł. NIE renderuj bez tokenu — skończyłoby
    // się falą 401 i chaotycznym twardym odbiciem. Pokaż błąd na ekranie logowania i przerwij
    // bootstrap (return PRZED render — inaczej kontekst i tak zamontuje się i /me nadpisze reason).
    window.location.href = buildCentralLoginUrl({ reason: 'handoff-failed' });
    return;
  }
  if (impersonation) {
    setAccessToken(impersonation.accessToken);
  }

  // ADR-0075 — multitenant handoff: `#__handoffKey=...` po centralnym logowaniu /
  // wyborze / przełączeniu firmy. Wymiana klucza na sesję zanim /me odpali.
  const handoff = await pickupHandoffKey();
  if (handoff && !handoff.ok) {
    // Handoff key BYŁ, ale redeem padł — analogicznie: przerwij bootstrap zanim /me da 401
    // i interceptor zrobi twarde odbicie z nadpisanym `reason`.
    window.location.href = buildCentralLoginUrl({ reason: 'handoff-failed' });
    return;
  }
  if (handoff) {
    setAccessToken(handoff.accessToken);
  }

  createRoot(root!).render(
    <StrictMode>
      <ThemeProvider>
        {/* Top-level ErrorBoundary — łapie katastroficzne błędy poza drzewem komponentów.
            Fallback: pełnoekranowy komunikat z przyciskiem "Odśwież stronę". */}
        <ErrorBoundary
          fallback={(error, reset) => (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bg-primary p-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <svg
                  className="h-8 w-8 text-red-600 dark:text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-text-primary">
                  Aplikacja napotkała błąd
                </h1>
                <p className="mt-1 max-w-sm text-sm text-text-secondary">
                  Coś poszło bardzo nie tak. Odśwież stronę, aby spróbować ponownie.
                </p>
                {import.meta.env.DEV && (
                  <pre className="mt-4 max-h-60 max-w-xl overflow-auto rounded-lg bg-gray-100 p-4 text-left text-xs text-red-700 dark:bg-gray-800 dark:text-red-400">
                    {error.message}
                    {error.stack ? '\n\n' + error.stack : ''}
                  </pre>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={reset}
                  className="rounded-lg bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700"
                >
                  Spróbuj ponownie
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-secondary"
                >
                  Odśwież stronę
                </button>
              </div>
            </div>
          )}
        >
          <QueryClientProvider client={queryClient}>
            <ToastProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
              {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
            </ToastProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </StrictMode>,
  );
}

void bootstrap();
